import { Check, Sparkles, TriangleAlert } from 'lucide-preact'
import type { ComponentChildren } from 'preact'
import { BRAND_CLASSES } from '@/constants/theme'
import type { EntryState } from '@/components/console/knowledgeBase/knowledgeBaseModel'

/**
 * Every row's columns: what the entry is, its state, and the chevron, so the states line up down the page. On a phone the
 * state drops under the name. The focus ring is neutral, as on table rows, because the brand green fails 3:1 on white.
 */
export const ENTRY_COLUMNS = 'grid-cols-[minmax(0,1fr)_16px] sm:grid-cols-[minmax(0,1fr)_176px_16px] gap-x-6'
export const ENTRY_ROW_GRID = `grid ${ENTRY_COLUMNS} items-start w-full py-3.5 text-left rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 dark:focus-visible:ring-white focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-800`
export const ENTRY_STATE_CELL = 'block col-start-1 row-start-2 mt-2 sm:mt-0 sm:col-start-2 sm:row-start-1'
export const ENTRY_CHEVRON_CELL = 'w-4 h-4 mt-[2.5px] col-start-2 row-start-1 sm:col-start-3'
export const ENTRY_NAME = 'block text-sm font-medium text-slate-900 dark:text-white'
export const ENTRY_DESCRIPTION = 'block max-w-[64ch] mt-0.5 text-sm text-slate-500 dark:text-slate-400'
/** The small line under a state: who confirmed it, or where the entry comes from. */
export const STATE_NOTE = 'block mt-0.5 pl-6 text-xs text-slate-500 dark:text-slate-400'

/** Lucide has no ring this small; an empty entry is an open circle inside the icon box. */
export function EmptyMark({ className }: { className: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true" className={className}>
      <circle cx="12" cy="12" r="7.5" />
    </svg>
  )
}

export function ReviewedMark({ className }: { className: string }) {
  return <Check className={`${className} text-green-600 dark:text-green-400`} strokeWidth={2.25} aria-hidden="true" />
}

export function DraftMark({ className }: { className: string }) {
  return <Sparkles className={`${className} text-purple-600 dark:text-purple-400`} aria-hidden="true" />
}

export function WarningMark({ className }: { className: string }) {
  return <TriangleAlert className={`${className} text-amber-600 dark:text-amber-400`} aria-hidden="true" />
}

function StateLine({ tone, children }: { tone: 'ok' | 'draft' | 'quiet'; children: ComponentChildren }) {
  const text = tone === 'draft' ? 'text-purple-700 dark:text-purple-300' : tone === 'ok' ? 'text-slate-600 dark:text-slate-300' : 'text-slate-500 dark:text-slate-400'
  return <span className={`flex items-center gap-2 text-sm ${text}`}>{children}</span>
}

/** A row's state in a word and a shape, never by color alone. */
export function EntryStateLabel({ state, draftLabel = 'Webster draft' }: { state: EntryState; draftLabel?: string }) {
  if (state === 'missing') {
    return (
      <span className={`inline-flex items-center gap-1.5 pl-2 pr-2.5 py-0.5 rounded-full text-xs font-medium ${BRAND_CLASSES.STATUS.WARNING}`}>
        <WarningMark className="w-3 h-3 flex-none" />Needed for email
      </span>
    )
  }
  if (state === 'draft') return <StateLine tone="draft"><DraftMark className="w-4 h-4 flex-none" />{draftLabel}</StateLine>
  if (state === 'filled') return <StateLine tone="ok"><ReviewedMark className="w-4 h-4 flex-none" />Reviewed</StateLine>
  return <StateLine tone="quiet"><EmptyMark className="w-4 h-4 flex-none text-slate-400 dark:text-slate-500" />Empty</StateLine>
}
